import { handleUnauthorized } from '@utils/apiHelpers'
import { store } from '@store/Store'

function dedupeOrdered(ids: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of ids) {
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(id)
  }
  return out
}

/** Same widget identity rules as dialog list normalization (channel id = key or numeric id string). */
function widgetChannelIdFromDialogRaw(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const widgetKeyRaw =
    r.widgetKey ??
    r.widget_key ??
    (r.widget as Record<string, unknown> | undefined)?.key ??
    (r.widget as Record<string, unknown> | undefined)?.widgetKey ??
    (r.widget as Record<string, unknown> | undefined)?.widget_key ??
    r.siteKey
  const widgetIdRaw =
    r.widgetId ??
    r.widget_id ??
    (r.widget as Record<string, unknown> | undefined)?.widget_id ??
    (r.widget as Record<string, unknown> | undefined)?.id ??
    r.siteId
  const key =
    widgetKeyRaw != null && String(widgetKeyRaw).trim() !== ''
      ? String(widgetKeyRaw).trim()
      : widgetIdRaw != null && String(widgetIdRaw).trim() !== ''
        ? String(widgetIdRaw).trim()
        : null
  return key
}

async function fetchDialogTabWidgetIds(
  baseUrl: string,
  headers: Record<string, string>,
  tab: 'inbox' | 'my' | 'archive',
): Promise<string[] | null> {
  const params = new URLSearchParams({ tab, page: '1', perPage: '100' })
  const res = await fetch(`${baseUrl}/api/operator/dialogs?${params.toString()}`, {
    method: 'GET',
    headers: { ...headers },
  })
  if (handleUnauthorized(res)) return null
  if (!res.ok) return []
  let json: any
  try {
    json = await res.json()
  } catch {
    return []
  }
  const rawList = json?.data?.items ?? json?.items ?? json?.data ?? []
  if (!Array.isArray(rawList)) return []
  const ids: string[] = []
  for (const raw of rawList) {
    const id = widgetChannelIdFromDialogRaw(raw)
    if (id) ids.push(id)
  }
  return ids
}

type WidgetIdsResult = { widgetIds: string[]; unauthorized: boolean }

/**
 * Dedupe near-simultaneous calls (shared Centrifuge init in DialogsCountersProvider
 * + both init paths in DialogsPage) so widget discovery does not fire the same
 * GET /api/widget (and dialog-list fallback) two or three times on page load.
 */
const WIDGET_IDS_CACHE_TTL_MS = 30_000
let widgetIdsInflight: { key: string; promise: Promise<WidgetIdsResult> } | null = null
let widgetIdsCache: { key: string; result: WidgetIdsResult; expiresAt: number } | null = null

function widgetIdsCacheKey(baseUrl: string, headers: Record<string, string>): string {
  return `${baseUrl.replace(/\/$/, '')}|${headers.Authorization ?? ''}`
}

/**
 * Widget ids used in Centrifugo channels (`widget.<id>.…`).
 * Prefers GET /api/widget; if that is empty or not allowed, derives ids from
 * /api/operator/dialogs (operators may lack widget admin access but still handle chats).
 *
 * Results are briefly cached and in-flight requests are shared so the concurrent
 * init paths on the dialogs page reuse a single network round-trip.
 */
export function fetchWidgetIdsForCentrifuge(
  baseUrl: string,
  headers: Record<string, string>,
): Promise<WidgetIdsResult> {
  const key = widgetIdsCacheKey(baseUrl, headers)

  const cached = widgetIdsCache
  if (cached && cached.key === key && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.result)
  }

  const inflight = widgetIdsInflight
  if (inflight && inflight.key === key) {
    return inflight.promise
  }

  const promise = fetchWidgetIdsForCentrifugeUncached(baseUrl, headers)
    .then((result) => {
      // Only cache successful (authorized) discoveries with at least one widget;
      // empty/unauthorized results should be retried on the next attempt.
      if (!result.unauthorized && result.widgetIds.length > 0) {
        widgetIdsCache = { key, result, expiresAt: Date.now() + WIDGET_IDS_CACHE_TTL_MS }
      }
      return result
    })
    .finally(() => {
      if (widgetIdsInflight?.promise === promise) widgetIdsInflight = null
    })

  widgetIdsInflight = { key, promise }
  return promise
}

async function fetchWidgetIdsForCentrifugeUncached(
  baseUrl: string,
  headers: Record<string, string>,
): Promise<WidgetIdsResult> {
  const b = baseUrl.replace(/\/$/, '')

  await store.userWidgets.ensureLoaded(b, headers)
  const fromStore = store.userWidgets.widgets
    .map((w) => (w.id != null ? String(w.id) : ''))
    .filter(Boolean)
  if (fromStore.length > 0) {
    return { widgetIds: dedupeOrdered(fromStore), unauthorized: false }
  }

  const inbox = await fetchDialogTabWidgetIds(b, headers, 'inbox')
  if (inbox === null) return { widgetIds: [], unauthorized: true }
  const my = await fetchDialogTabWidgetIds(b, headers, 'my')
  if (my === null) return { widgetIds: [], unauthorized: true }
  const archive = await fetchDialogTabWidgetIds(b, headers, 'archive')
  if (archive === null) return { widgetIds: [], unauthorized: true }

  return { widgetIds: dedupeOrdered([...inbox, ...my, ...archive]), unauthorized: false }
}
